import type { LngLat } from "../adapters/types";

const METERS_PER_DEGREE = 111_320;

/**
 * Hauteur d'une volée le long de son axe, du haut vers le bas : des couples (distance depuis
 * l'extrémité du haut, hauteur), en mètres, distances croissantes. Paliers compris.
 */
export type FlightProfile = [number, number][];

/** Un escalier ou un escalator posé sur la carte, vu comme un sol en pente. */
export interface DecorFlight {
  position: LngLat;
  /** Cap vers lequel la volée descend. */
  rotationDeg: number;
  scale: number;
  /** Emprise dans le repère du modèle, en mètres : en travers (x), et le long de l'axe (y, vers le bas). */
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  profile: FlightProfile;
  /** Hauteur de la volée tout entière, ajoutée au profil. */
  base: number;
  escalator: boolean;
}

function profileHeight(profile: FlightProfile, distance: number): number {
  const first = profile[0]!;
  if (distance <= first[0]) return first[1];
  for (let i = 1; i < profile.length; i++) {
    const [d1, h1] = profile[i]!;
    if (distance > d1) continue;
    const [d0, h0] = profile[i - 1]!;
    return d1 === d0 ? h1 : h0 + ((h1 - h0) * (distance - d0)) / (d1 - d0);
  }
  return profile[profile.length - 1]![1];
}

/** Hauteur de la volée sous ce point, ou `null` hors de son emprise. */
export function flightHeightAt(point: LngLat, flight: DecorFlight): number | null {
  const metersPerLng = METERS_PER_DEGREE * Math.cos((flight.position[1] * Math.PI) / 180);
  const east = (point[0] - flight.position[0]) * metersPerLng;
  const north = (point[1] - flight.position[1]) * METERS_PER_DEGREE;
  const angle = (flight.rotationDeg * Math.PI) / 180;
  const along = (east * Math.sin(angle) + north * Math.cos(angle)) / flight.scale;
  const across = (east * Math.cos(angle) - north * Math.sin(angle)) / flight.scale;
  if (across < flight.minX || across > flight.maxX || along < flight.minY || along > flight.maxY) return null;
  return flight.base + profileHeight(flight.profile, along - flight.minY) * flight.scale;
}

/** Au-delà de cet écart avec l'axe d'une volée, on la traverse sans l'emprunter. */
const FLIGHT_MAX_HEADING_GAP_DEG = 35;

/**
 * La volée sous ce point et sa hauteur ; la plus haute quand deux se chevauchent. Avec un cap, seules
 * comptent les volées que l'on emprunte dans leur sens, en montant ou en descendant : un passant qui
 * coupe un escalator en travers ne le prend pas.
 */
export function flightAt(point: LngLat, flights: DecorFlight[], headingDeg?: number): { flight: DecorFlight; height: number } | null {
  let best: { flight: DecorFlight; height: number } | null = null;
  for (const flight of flights) {
    if (headingDeg !== undefined && Math.abs(Math.cos(((headingDeg - flight.rotationDeg) * Math.PI) / 180)) < Math.cos((FLIGHT_MAX_HEADING_GAP_DEG * Math.PI) / 180)) continue;
    const height = flightHeightAt(point, flight);
    if (height !== null && (!best || height > best.height)) best = { flight, height };
  }
  return best;
}
